// ── Browser storage key constants ─────────────────────────────────────────
import { GIS_TOKEN_EXPIRATION } from '@app/modules/gis/gis-config';

export const GIS_ACCESS_TOKEN_KEY         = 'geobrain_gis_access_token';
export const GIS_TOKEN_EXPIRY_KEY         = 'geobrain_gis_token_expiry';
export const GIS_REFRESH_TOKEN_KEY        = 'geobrain_gis_refresh_token';
export const GIS_REFRESH_TOKEN_EXPIRY_KEY = 'geobrain_gis_refresh_token_expiry';
export const GIS_PORTAL_URL_KEY           = 'geobrain_gis_portal_url';
export const GIS_PORTAL_APPID_KEY         = 'geobrain_gis_portal_appid';

// Keys written before a PKCE redirect — read and cleared by main.ts on return.
const GIS_VERIFIER_KEY   = 'geobrain_gis_code_verifier';
const GIS_PKCE_APPID_KEY = 'geobrain_gis_app_id';       // main.ts reads this key
const GIS_RETURN_URL_KEY = 'geobrain_gis_return_url';

// Deduplicates concurrent refresh attempts — all callers await the same promise.
let _refreshPromise: Promise<boolean> | null = null;

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Returns true if a non-expired GIS access token exists in sessionStorage.
 * Clears stale token entries when expired.
 */
export function hasValidGisToken(): boolean {
  const token  = sessionStorage.getItem(GIS_ACCESS_TOKEN_KEY);
  const expiry = parseInt(sessionStorage.getItem(GIS_TOKEN_EXPIRY_KEY) || '0', 10);
  if (token && expiry && Date.now() < expiry) { return true; }
  sessionStorage.removeItem(GIS_ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(GIS_TOKEN_EXPIRY_KEY);
  return false;
}

/**
 * Returns a valid GIS access token.
 * If the current token is expired, attempts a silent refresh first.
 * If refresh also fails (refresh token gone/expired), restarts the full
 * PKCE auth flow — redirecting the user back to the portal so they can
 * re-authenticate mid-session without needing a route navigation.
 * Returns an empty string only if the portal config is not yet stored
 * (i.e. the guard hasn't run yet — it will handle first-time auth itself).
 */
export async function getGisToken(): Promise<string> {
  if (hasValidGisToken()) {
    return sessionStorage.getItem(GIS_ACCESS_TOKEN_KEY)!;
  }
  const refreshed = await refreshGisToken();
  if (!refreshed) {
    await startGisPkceFlow();
    return '';
  }
  return sessionStorage.getItem(GIS_ACCESS_TOKEN_KEY) || '';
}

/**
 * Starts a full PKCE authorization redirect.
 * Reads the portal URL and appId from sessionStorage (stored by storeGisPortalConfig).
 * If either is missing the guard has not yet run — returns without redirecting
 * so the guard can handle first-time auth on the next route activation.
 * Saves the current path as the post-auth return URL.
 */
export async function startGisPkceFlow(): Promise<void> {
  const portalUrl = sessionStorage.getItem(GIS_PORTAL_URL_KEY);
  const appId     = sessionStorage.getItem(GIS_PORTAL_APPID_KEY);
  if (!portalUrl || !appId) { return; }

  const codeVerifier  = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  sessionStorage.setItem(GIS_VERIFIER_KEY,   codeVerifier);
  sessionStorage.setItem(GIS_PKCE_APPID_KEY, appId);
  sessionStorage.setItem(GIS_RETURN_URL_KEY, window.location.pathname + window.location.search + window.location.hash);

  const params = new URLSearchParams({
    client_id:             appId,
    response_type:         'code',
    redirect_uri:          window.location.origin + '/',
    code_challenge:        codeChallenge,
    code_challenge_method: 'S256',
    expiration:            String(GIS_TOKEN_EXPIRATION)
  });

  window.location.href = `${portalUrl}/sharing/oauth2/authorize?${params}`;
}

function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, b => ('0' + b.toString(16)).slice(-2)).join('');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const data   = new TextEncoder().encode(verifier);
  const sha256 = await crypto.subtle.digest('SHA-256', data);
  let str = '';
  new Uint8Array(sha256).forEach(b => str += String.fromCharCode(b));
  return btoa(str)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Synchronously returns whatever GIS token is currently in sessionStorage.
 * Use only for synchronous Leaflet layer construction (createDynamicMapLayer,
 * createFeatureLayer) where awaiting is not possible.  The guard ensures a
 * valid token is present before any map route loads.
 */
export function getGisTokenSync(): string {
  if (hasValidGisToken()) {
    return sessionStorage.getItem(GIS_ACCESS_TOKEN_KEY)!;
  }
  // Token expired: trigger a silent background refresh so the new token is
  // stored in sessionStorage by the time the next sync call is made.
  // The current call returns '' — the Leaflet layer will retry on its next tick.
  refreshGisToken();
  return '';
}

/**
 * Attempts a silent token refresh using the stored refresh token.
 * Uses fetchWithRetry for automatic exponential backoff on transient errors
 * (network failures, 5xx, 429). Only permanent auth failures clear the token.
 * Returns true if a new access token was successfully obtained.
 * Concurrent calls share a single in-flight request.
 */
export function refreshGisToken(): Promise<boolean> {
  if (_refreshPromise) { return _refreshPromise; }

  _refreshPromise = (async (): Promise<boolean> => {
    let refreshOutcome = 'unknown';

    try {
      const refreshToken = sessionStorage.getItem(GIS_REFRESH_TOKEN_KEY);
      const portalUrl    = sessionStorage.getItem(GIS_PORTAL_URL_KEY);
      const appId        = sessionStorage.getItem(GIS_PORTAL_APPID_KEY);

      const rtExpiry = parseInt(sessionStorage.getItem(GIS_REFRESH_TOKEN_EXPIRY_KEY) || '0', 10);
      if (!refreshToken || !portalUrl || !appId) {
        refreshOutcome = 'missing_refresh_context';
        return false;
      }
      if (rtExpiry && Date.now() >= rtExpiry) {
        sessionStorage.removeItem(GIS_REFRESH_TOKEN_KEY);
        sessionStorage.removeItem(GIS_REFRESH_TOKEN_EXPIRY_KEY);
        refreshOutcome = 'refresh_token_expired';
        return false;
      }

      const body = new URLSearchParams({
        client_id:     appId,
        refresh_token: refreshToken,
        grant_type:    'refresh_token',
        redirect_uri:  window.location.origin + '/'
      });

      const res  = await fetchWithRetry(`${portalUrl}/sharing/oauth2/token?f=json`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body:    body.toString()
      });

      // Transient upstream failures: keep refresh token for later retry.
      if (res.status === 429 || res.status >= 500) {
        refreshOutcome = `transient_http_${res.status}`;
        return false;
      }

      // Permanent auth/client failures: refresh token is no longer usable.
      if (res.status >= 400 && res.status < 500) {
        if (hasValidGisToken()) {
          refreshOutcome = `client_http_${res.status}_fallback_token_present`;
          return true;
        }
        sessionStorage.removeItem(GIS_REFRESH_TOKEN_KEY);
        sessionStorage.removeItem(GIS_REFRESH_TOKEN_EXPIRY_KEY);
        refreshOutcome = `client_http_${res.status}`;
        return false;
      }

      const data = await res.json();

      if (data.access_token) {
        sessionStorage.setItem(GIS_ACCESS_TOKEN_KEY,  data.access_token);
        sessionStorage.setItem(GIS_TOKEN_EXPIRY_KEY,  (Date.now() + data.expires_in * 1000).toString());
        if (data.refresh_token) {
          sessionStorage.setItem(GIS_REFRESH_TOKEN_KEY, data.refresh_token);
          if (data.refresh_token_expires_in) {
            sessionStorage.setItem(GIS_REFRESH_TOKEN_EXPIRY_KEY,
              (Date.now() + data.refresh_token_expires_in * 1000).toString());
          }
        }
        refreshOutcome = 'refreshed_access_token';
        return true;
      }

      // Any error response from the portal means the refresh token is no longer usable.
      // A concurrent tab may have already rotated it and stored a fresh access token.
      if (hasValidGisToken()) {
        refreshOutcome = 'portal_error_but_existing_token_present';
        return true;
      }
      refreshOutcome = 'portal_response_missing_access_token';
      return false;
    } catch (err) {
      console.error('refreshGisToken: unexpected error while refreshing GIS token', {
        error: err,
        hasAccessToken: !!sessionStorage.getItem(GIS_ACCESS_TOKEN_KEY),
        hasRefreshToken: !!sessionStorage.getItem(GIS_REFRESH_TOKEN_KEY),
        portalUrl: sessionStorage.getItem(GIS_PORTAL_URL_KEY),
        appId: sessionStorage.getItem(GIS_PORTAL_APPID_KEY)
      });
      refreshOutcome = 'exception';
      return false;
    } finally {
      console.debug('refreshGisToken: finished', {
        outcome: refreshOutcome,
        hasValidToken: hasValidGisToken(),
        hasRefreshToken: !!sessionStorage.getItem(GIS_REFRESH_TOKEN_KEY)
      });
      _refreshPromise = null;
    }
  })();

  return _refreshPromise;
}

/**
 * Persists the portal config needed for silent refresh and mid-session
 * PKCE restarts (called by GisAuthGuard on every canActivate).
 */
export function storeGisPortalConfig(portalUrl: string, appId: string): void {
  sessionStorage.setItem(GIS_PORTAL_URL_KEY,   portalUrl);
  sessionStorage.setItem(GIS_PORTAL_APPID_KEY, appId);
}

// ── Transient Error Retry Logic ───────────────────────────────────────────
/**
 * Retries a fetch operation with exponential backoff for transient errors only.
 * Makes up to 3 attempts with exponential delays between retries: 1s, 2s, 4s.
 *
 * Only retries on transient failures:
 *  - Network errors (fetch throws)
 *  - 5xx server errors
 *
 * Does NOT retry on 4xx client errors (they're permanent).
 */
export async function fetchWithRetry(
  url: string,
  options: RequestInit
): Promise<Response> {
  let lastError: Error | null = null;
  const maxAttempts = 3;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const res = await fetch(url, options);

      // Don't retry 4xx errors (they're permanent)
      if (res.status >= 400 && res.status < 500) {
        return res;
      }

      // Retry 5xx errors
      if (res.status >= 500) {
        lastError = new Error(`Server error: ${res.status}`);
        if (attempt < maxAttempts) {
          const delayMs = Math.pow(2, attempt - 1) * 1000; // 1s, 2s, 4s
          await new Promise(resolve => setTimeout(resolve, delayMs));
          continue;
        }
        return res;
      }

      // 2xx/3xx — success, return immediately
      return res;
    } catch (err) {
      // Network error — retry with backoff
      lastError = err as Error;
      if (attempt < maxAttempts) {
        const delayMs = Math.pow(2, attempt - 1) * 1000;
        await new Promise(resolve => setTimeout(resolve, delayMs));
        continue;
      }
      throw err;
    }
  }

  // Should not reach here, but throw last error if we do
  throw lastError || new Error('Fetch with retry failed');
}

// ── Auth Retry Counter (sessionStorage, per-tab) ────────────────────────────
const GIS_AUTH_RETRY_COUNT_KEY = 'geobrain_gis_auth_retry_count';

/**
 * Increments the auth retry counter and returns the new value.
 * Used to track consecutive auth failures and eventually redirect to error page.
 */
export function incrementGisAuthRetryCounter(): number {
  const current = parseInt(sessionStorage.getItem(GIS_AUTH_RETRY_COUNT_KEY) || '0', 10);
  const next = current + 1;
  sessionStorage.setItem(GIS_AUTH_RETRY_COUNT_KEY, String(next));
  return next;
}

/**
 * Gets the current auth retry counter value (0 if never attempted).
 */
export function getGisAuthRetryCount(): number {
  return parseInt(sessionStorage.getItem(GIS_AUTH_RETRY_COUNT_KEY) || '0', 10);
}

/**
 * Clears the auth retry counter (called on successful auth or user retry).
 */
export function clearGisAuthRetryCounter(): void {
  sessionStorage.removeItem(GIS_AUTH_RETRY_COUNT_KEY);
}
